'use client';

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiUser, FiClock, FiAlertCircle } from 'react-icons/fi';
import { tw } from '../lib/twind';
import type { CampaignCallInfo } from '@callixbrasil/client-sdk';

type FieldConfig = {
  name: string;
  code: string;
  width: string;
  required: boolean;
  editable: boolean;
  maxLength: number | null;
  type: string;
  label: boolean;
  listItems: unknown[] | null;
  tableColumns: unknown[] | null;
  tableVisibleLines: number;
  allowZeros: boolean;
};

type CampaignFieldsResponse = {
  rows: FieldConfig[][];
  labelField: { code: string; type: number } | null;
  phoneFields: { code: string; name: string; editable: boolean }[];
};

type ContactValues = Record<string, string>;

type DragPosition = {
  x: number;
  y: number;
};

type PointerData = {
  pointerId: number | null;
  offsetX: number;
  offsetY: number;
};

type DraggableClientInfoModalProps = {
  isOpen: boolean;
  campaignCallInfo: CampaignCallInfo | null;
  callDuration: number;
  isCallActive: boolean;
  onClose: () => void;
};

const VIEWPORT_MARGIN = 16;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// URL base para as APIs - deve apontar para o servidor Next.js
const API_BASE_URL = typeof window !== 'undefined'
  ? ((window as unknown) as Record<string, unknown>).__CALLIX_API_BASE_URL__ as string || 'https://callix-zeta.vercel.app'
  : 'https://callix-zeta.vercel.app';

/**
 * Get session token from localStorage
 * Pattern: callix_dialer_{username}_session_token
 */
function getSessionToken(): string | null {
  if (typeof window === 'undefined') return null;

  console.log('[DraggableClientInfoModal] 🔍 Buscando token de sessão no localStorage...');

  // First, get the last username used
  const lastUsername = localStorage.getItem('callix_dialer_last_username');

  if (lastUsername) {
    const sessionKey = `callix_dialer_${lastUsername}_session_token`;
    const token = localStorage.getItem(sessionKey);

    if (token) {
      console.log(`[DraggableClientInfoModal] ✅ Token encontrado para usuário "${lastUsername}":`, token.substring(0, 20) + '...');
      return token;
    } else {
      console.log(`[DraggableClientInfoModal] ⚠️ Chave de sessão "${sessionKey}" não encontrada`);
    }
  } else {
    console.log('[DraggableClientInfoModal] ⚠️ callix_dialer_last_username não encontrado');
  }

  // Fallback: search for any callix_dialer_*_session_token key
  console.log('[DraggableClientInfoModal] 🔍 Procurando qualquer chave de sessão...');
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.match(/^callix_dialer_.*_session_token$/)) {
      const token = localStorage.getItem(key);
      if (token) {
        console.log(`[DraggableClientInfoModal] ✅ Token encontrado na chave "${key}":`, token.substring(0, 20) + '...');
        return token;
      }
    }
  }

  // Debug: list all localStorage keys
  console.log('[DraggableClientInfoModal] ⚠️ Nenhum token encontrado. Chaves disponíveis no localStorage:');
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      console.log(`  - ${key}`);
    }
  }

  return null;
}

/**
 * Get headers with authentication token
 */
function getAuthHeaders(): HeadersInit {
  const token = getSessionToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['x-session-token'] = token;
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Fetch com retry automático e logs detalhados
 */
async function fetchWithRetry<T>(
  url: string,
  options: RequestInit,
  retries = 3,
  backoff = 1000
): Promise<T> {
  console.log(`[DraggableClientInfoModal] 🚀 Iniciando fetch: ${url}`);

  for (let i = 0; i < retries; i++) {
    try {
      console.log(`[DraggableClientInfoModal] 📡 Tentativa ${i + 1}/${retries}`);
      const startTime = Date.now();

      const response = await fetch(url, options);

      const duration = Date.now() - startTime;
      console.log(`[DraggableClientInfoModal] ⏱️ Resposta em ${duration}ms - Status: ${response.status}`);

      if (!response.ok) {
        // Se for erro 4xx (cliente), não vale retry
        if (response.status >= 400 && response.status < 500) {
          const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
          console.error(`[DraggableClientInfoModal] ❌ Erro do cliente (${response.status}):`, error);
          throw new Error(error.error || `Falha: ${response.status}`);
        }

        // Para erros 5xx (servidor), fazer retry
        console.warn(`[DraggableClientInfoModal] ⚠️ Erro do servidor (${response.status}), tentando novamente...`);
        throw new Error(`HTTP ${response.status}`);
      }

      const json = await response.json();
      console.log(`[DraggableClientInfoModal] ✅ Dados recebidos com sucesso`);
      return json as T;

    } catch (error) {
      const isLastAttempt = i === retries - 1;

      if (isLastAttempt) {
        console.error(`[DraggableClientInfoModal] 💥 Falha final após ${retries} tentativas:`, error);
        throw error;
      }

      const delay = backoff * Math.pow(2, i); // Backoff exponencial
      console.warn(`[DraggableClientInfoModal] ⏳ Aguardando ${delay}ms antes da próxima tentativa...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw new Error('Todas as tentativas falharam');
}

/**
 * Fetches campaign configuration to get campaignModelId
 */
async function getCampaignConfig(contactId: number): Promise<{ campaignModelId: number }> {
  const url = `${API_BASE_URL}/api/campaign-config/${contactId}`;

  return fetchWithRetry<{ campaignModelId: number }>(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });
}

/**
 * Fetches campaign fields structure (rows + mapping)
 */
async function getCampaignFields(campaignModelId: number): Promise<CampaignFieldsResponse> {
  const url = `${API_BASE_URL}/api/campaign-fields/${campaignModelId}`;

  const json = await fetchWithRetry<CampaignFieldsResponse>(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });

  console.log('[DraggableClientInfoModal] Estrutura de campos recebida:', json);

  // Garantir que a estrutura está correta
  if (!json.rows || !Array.isArray(json.rows)) {
    console.warn('[DraggableClientInfoModal] Estrutura de campos inválida, usando fallback');
    return {
      rows: [],
      labelField: null,
      phoneFields: []
    };
  }

  return json;
}

// CUSTOMER-MODE: estrutura de campos via form-rows/customer/{formId}
async function getCustomerFields(formId: number): Promise<CampaignFieldsResponse> {
  const url = `${API_BASE_URL}/api/campaign-contact/0?source=customer&formId=${encodeURIComponent(String(formId))}`;

  const json = await fetchWithRetry<CampaignFieldsResponse>(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });

  console.log('[DraggableClientInfoModal] (cliente) Estrutura de campos recebida:', json);

  if (!json.rows || !Array.isArray(json.rows)) {
    console.warn('[DraggableClientInfoModal] (cliente) Estrutura de campos inválida, usando fallback');
    return { rows: [], labelField: null, phoneFields: [] };
  }

  return json;
}

/**
 * Fetches contact values
 */
async function getContactValues(contactId: number): Promise<ContactValues> {
  const url = `${API_BASE_URL}/api/campaign-contact/${contactId}`;

  return fetchWithRetry<ContactValues>(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });
}

// CUSTOMER-MODE: valores via form-data/customer/{customerId}
async function getCustomerContactValues(customerId: number): Promise<ContactValues> {
  const url = `${API_BASE_URL}/api/campaign-fields/0?source=customer&customerId=${encodeURIComponent(String(customerId))}`;

  return fetchWithRetry<ContactValues>(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'omit',
  });
}

export function DraggableClientInfoModal({
  isOpen,
  campaignCallInfo,
  callDuration,
  isCallActive,
  onClose,
}: DraggableClientInfoModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<PointerData>({ pointerId: null, offsetX: 0, offsetY: 0 });
  const dragMovedRef = useRef(false);

  const [position, setPosition] = useState<DragPosition>({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);

  // Estado para dados do contato
  const [contactData, setContactData] = useState<ContactValues>({});
  const [fieldRows, setFieldRows] = useState<FieldConfig[][]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedContactId, setFetchedContactId] = useState<number | null>(null);

  // Extrair campaignContact (igual ao CallInfoDisplay)
  const campaignContact = campaignCallInfo?.campaignContact;

  const clampPositionToViewport = useCallback((proposed: DragPosition) => {
    if (typeof window === 'undefined') {
      return proposed;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const element = containerRef.current;
    const width = element?.offsetWidth ?? 500;
    const height = element?.offsetHeight ?? 600;

    const minX = VIEWPORT_MARGIN;
    const minY = VIEWPORT_MARGIN;
    const maxX = Math.max(minX, viewportWidth - width - VIEWPORT_MARGIN);
    const maxY = Math.max(minY, viewportHeight - height - VIEWPORT_MARGIN);

    return {
      x: clamp(proposed.x, minX, maxX),
      y: clamp(proposed.y, minY, maxY),
    };
  }, []);

  // Centralizar modal ao abrir
  useEffect(() => {
    if (isOpen && typeof window !== 'undefined') {
      const element = containerRef.current;
      const width = element?.offsetWidth ?? 500;
      const height = element?.offsetHeight ?? 600;
      const centerX = (window.innerWidth - width) / 2;
      const centerY = (window.innerHeight - height) / 2;
      setPosition(clampPositionToViewport({ x: centerX, y: centerY }));
    }
  }, [isOpen, clampPositionToViewport]);

  // Ajustar posição ao redimensionar viewport
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return;
    }

    const handleViewportChange = () => {
      setPosition((prev) => clampPositionToViewport(prev));
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [isOpen, clampPositionToViewport]);

  // Buscar dados do contato
  useEffect(() => {
    if (!campaignContact?.id) {
      return;
    }

    // Skip if already fetched this contact
    if (fetchedContactId === campaignContact.id) {
      return;
    }

    let cancelled = false;

    const fetchContactData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const customer = (campaignContact as unknown as { customer?: { id?: number; formId?: number } }).customer;
        console.log('[DraggableClientInfoModal] Verificando customer em campaignContact:', customer);

        let campaignFields: CampaignFieldsResponse;
        let contactValues: ContactValues;

        if (customer?.id && customer?.formId) {
          // MODO CLIENTE: NÃO chamar getCampaignConfig, usar endpoints customer
          console.log('[DraggableClientInfoModal] Modo CUSTOMER: carregando form-rows (formId) e form-data (customerId)');
          [campaignFields, contactValues] = await Promise.all([
            getCustomerFields(customer.formId),
            getCustomerContactValues(customer.id),
          ]);
        } else {
          console.log('[DraggableClientInfoModal] Modo CAMPANHA: buscando config, campos e valores');
          // Step 1: Get campaign config to find campaignModelId
          const config = await getCampaignConfig(campaignContact.id);
          console.log('[DraggableClientInfoModal] Config recebida:', config);

          if (!config.campaignModelId) {
            throw new Error('campaignModelId não encontrado');
          }

          // Step 2: Fetch fields structure and values in parallel
          [campaignFields, contactValues] = await Promise.all([
            getCampaignFields(config.campaignModelId),
            getContactValues(campaignContact.id),
          ]);
        }

        console.log('[DraggableClientInfoModal] Campos recebidos:', campaignFields);
        console.log('[DraggableClientInfoModal] Valores recebidos:', contactValues);

        if (!cancelled) {
          console.log('[DraggableClientInfoModal] ✅ Dados carregados com sucesso');
          // Manter os valores com códigos como chave
          // A renderização irá usar o código para buscar o valor
          setContactData(contactValues);
          setFieldRows(campaignFields.rows || []);
          setFetchedContactId(campaignContact.id);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[DraggableClientInfoModal] ❌ Erro ao buscar dados:', err);
          const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar dados do contato';
          setError(errorMessage);
          setIsLoading(false);
        }
      }
    };

    void fetchContactData();

    return () => {
      cancelled = true;
    };
  }, [campaignContact, campaignContact?.id, fetchedContactId]);

  // Gerenciar drag
  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (pointerRef.current.pointerId !== event.pointerId) {
        return;
      }

      dragMovedRef.current = true;

      const modalWidth = containerRef.current?.offsetWidth ?? 0;
      const modalHeight = containerRef.current?.offsetHeight ?? 0;
      const nextX = event.clientX - pointerRef.current.offsetX;
      const nextY = event.clientY - pointerRef.current.offsetY;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
      const maxX = viewportWidth - modalWidth - VIEWPORT_MARGIN;
      const maxY = viewportHeight - modalHeight - VIEWPORT_MARGIN;
      const safeMaxX = Math.max(VIEWPORT_MARGIN, maxX);
      const safeMaxY = Math.max(VIEWPORT_MARGIN, maxY);

      setPosition({
        x: clamp(nextX, VIEWPORT_MARGIN, safeMaxX),
        y: clamp(nextY, VIEWPORT_MARGIN, safeMaxY),
      });
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (pointerRef.current.pointerId !== event.pointerId) {
        return;
      }

      setIsDragging(false);
      pointerRef.current = { pointerId: null, offsetX: 0, offsetY: 0 };
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);

  const beginDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.pointerType !== 'touch') {
      return;
    }

    const element = containerRef.current;
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    pointerRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    dragMovedRef.current = false;
    setIsDragging(true);
  }, []);

  const handleCloseClick = useCallback(() => {
    if (!isCallActive && onClose) {
      onClose();
    }
  }, [isCallActive, onClose]);

  if (!isOpen) {
    return null;
  }

  const hasData = Object.keys(contactData).length > 0;
  const hasFieldStructure = fieldRows && fieldRows.length > 0;

  // Helper para buscar valores pelo código
  const getFieldValue = (fieldCode: string): string => {
    return contactData[fieldCode] || '-';
  };

  const modalContent = (
    <div
      ref={containerRef}
      className={tw`fixed z-[9999] flex flex-col overflow-hidden rounded-xl border border-slate-300 bg-white shadow-2xl`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '520px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 32px)',
      }}
    >
      {/* Header - Área arrastável */}
      <div
        onPointerDown={beginDrag}
        className={tw`flex cursor-move items-center justify-between border-b border-slate-200 bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-white`}
      >
        <div className={tw`flex items-center gap-2`}>
          <FiUser className={tw`h-5 w-5`} />
          <h3 className={tw`text-sm font-bold`}>Informações da Campanha</h3>
        </div>
        <button
          type="button"
          onClick={handleCloseClick}
          disabled={isCallActive}
          className={tw`rounded-full p-1 transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40`}
          title={isCallActive ? 'Aguarde o fim da chamada para fechar' : 'Fechar'}
        >
          <FiX className={tw`h-5 w-5`} />
        </button>
      </div>

      {/* Body */}
      <div className={tw`flex-1 space-y-3 overflow-y-auto p-4`}>
        {/* Duração e informações básicas */}
        <div className={tw`grid grid-cols-2 gap-3`}>
          <div className={tw`flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2`}>
            <FiClock className={tw`h-4 w-4 text-slate-500`} />
            <div>
              <p className={tw`text-[10px] text-slate-500 uppercase tracking-wide font-medium`}>Duração</p>
              <p className={tw`text-sm font-bold text-slate-800`}>{formatDuration(callDuration)}</p>
            </div>
          </div>
          <div className={tw`flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2`}>
            <FiUser className={tw`h-4 w-4 text-slate-500`} />
            <div className={tw`min-w-0 flex-1`}>
              <p className={tw`text-[10px] text-slate-500 uppercase tracking-wide font-medium`}>Cliente</p>
              <p className={tw`truncate text-sm font-bold text-slate-800`}>
                {campaignContact?.label || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Estados de loading e erro */}
        {isLoading && (
          <div className={tw`rounded-lg border border-blue-200 bg-blue-50 p-6 text-center`}>
            <div className={tw`mx-auto mb-2 inline-block h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent`}></div>
            <p className={tw`text-xs font-medium text-blue-700`}>Carregando dados do contato...</p>
          </div>
        )}

        {error && (
          <div className={tw`rounded-lg border border-red-200 bg-red-50 p-3`}>
            <div className={tw`flex items-center gap-2`}>
              <FiAlertCircle className={tw`h-4 w-4 flex-shrink-0 text-red-600`} />
              <div>
                <p className={tw`text-xs font-semibold text-red-700`}>Erro ao carregar dados</p>
                <p className={tw`text-[10px] text-red-600`}>{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Dados do contato - LAYOUT DINÂMICO BASEADO NA ESTRUTURA DE ROWS */}
        {!isLoading && !error && hasData && hasFieldStructure && (
          <div className={tw`space-y-2`}>
            {fieldRows.map((row, rowIndex) => {
              // Calcular o número de colunas baseado na quantidade de campos
              const numFields = row.length;
              let gridCols = 'grid-cols-1'; // default
              
              if (numFields === 2) {
                gridCols = 'grid-cols-2';
              } else if (numFields === 3) {
                gridCols = 'grid-cols-3';
              } else if (numFields === 4) {
                gridCols = 'grid-cols-4';
              } else if (numFields === 5) {
                gridCols = 'grid-cols-5';
              }

              return (
                <div
                  key={rowIndex}
                  className={tw`grid gap-2 ${gridCols}`}
                >
                  {row.map((field, fieldIndex) => (
                    <Field
                      key={`${rowIndex}-${fieldIndex}-${field.code}`}
                      label={field.name}
                      value={getFieldValue(field.code)}
                      fullWidth={field.width === '100%'}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && !error && !hasData && (
          <div className={tw`flex flex-col items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-8 text-center`}>
            <FiUser className={tw`mb-2 h-8 w-8 text-slate-400`} />
            <p className={tw`text-sm text-slate-500`}>Nenhum dado disponível</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {isCallActive && (
        <div className={tw`border-t border-slate-200 bg-slate-50 px-4 py-2`}>
          <p className={tw`text-center text-xs text-slate-500`}>
            Aguarde o fim da chamada para fechar
          </p>
        </div>
      )}
    </div>
  );

  // Renderizar modal como primeiro filho do body usando portal
  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return null;
}

// Componente auxiliar para campos
function Field({ 
  label, 
  value, 
  fullWidth 
}: { 
  label: string; 
  value: string; 
  fullWidth?: boolean;
}) {
  // Adicionar estilo especial para campos full width
  const widthClass = fullWidth ? 'col-span-full' : '';

  return (
    <div className={tw`rounded-lg border border-slate-200 bg-white p-2 ${widthClass}`}>
      <p className={tw`mb-0.5 truncate text-[9px] font-semibold uppercase tracking-wide text-slate-500`}>
        {label}
      </p>
      <p className={tw`break-words text-xs font-semibold text-slate-800 line-clamp-2`}>
        {value}
      </p>
    </div>
  );
}
